import { createSlice, createSelector } from '@reduxjs/toolkit';

// ================================ Types ================================ //

/**
 * @typedef {import('@/types/api/NoticeComponent').NoticeComponentRecords} NoticeRecord
 */

/**
 * @typedef {{
 *  noticeRecords: NoticeRecord[],
 *  noticeContextMenu: {
 *    notice: import('@/types/api/NoticeRecord').NoticeRecord | import('@/types/api/NoticeRecord').NoticeRecordListItem | import('@/types/api/NoticeRecord').MaintenanceNoticeRecord | null,
 *    position: { x: number; y: number } | null
 *  }
 * }} NoticesState
 */

// ================================ Initial State ================================ //

/**
 * @type {NoticesState}
 */
const initialState = {
  noticeRecords: [],
  noticeContextMenu: {
    notice: null,
    position: null,
  },
};

// ============================= Public Methods ============================= //

/**
 *
 * @param {NoticeRecord | import('@/types/api/NoticeRecord').NoticeRecord | import('@/types/api/NoticeRecord').NoticeRecordListItem | import('@/types/api/NoticeRecord').MaintenanceNoticeRecord} notice
 * @returns
 */
export const getRawNoticeUrl = (notice) =>
  notice
    ? `${process.env.REACT_APP_API_URL}/notices/${notice.notice_file_path}`
    : '';

// ================================ Reducers ================================ //

/**
 * @param {NoticesState} initialState
 */
const resetState = (initialState) => initialState;

/**
 *
 * @param {NoticesState} state
 * @param {{
 *  payload: {
 *    noticeRecords: NoticeRecord[]
 *  }
 * }} action
 * @returns {void}
 */
const setNoticeRecords = (state, action) => {
  state.noticeRecords = action.payload.noticeRecords;
};

/**
 *
 * @param {NoticesState} state
 * @param {import('@reduxjs/toolkit').PayloadAction<NoticesState['noticeContextMenu'] | null>} action
 * @returns {void}
 */
const setNoticeContextMenu = (state, action) => {
  state.noticeContextMenu = action.payload ?? {
    notice: null,
    position: null,
  };
};

/**
 *
 * @param {NoticesState} initialState
 */
const _noticeReducers = (initialState) => ({
  resetState: () => resetState(initialState),
  setNoticeRecords,
  setNoticeContextMenu,
});

const reducers = _noticeReducers(initialState);

/** @type {import('@reduxjs/toolkit').Slice<NoticesState, typeof reducers, 'notices'>} */
export const noticeSlice = createSlice({
  name: 'notices',
  initialState,
  reducers,
});

export const selectNoticeRecords = createSelector(
  /**
   *
   * @param {{
   *  notices: NoticesState
   * }} state
   * @returns
   */
  (state) => state.notices.noticeRecords,
  (noticeRecords) => noticeRecords ?? [],
);

export const selectNoticeContextMenu = createSelector(
  /**
   *
   * @param {{
   *  notices: NoticesState
   * }} state
   * @returns
   */
  (state) => state.notices.noticeContextMenu,
  (noticeContextMenu) => noticeContextMenu ?? { notice: null, position: null },
);

export const noticeActions = noticeSlice.actions;

export default noticeSlice.reducer;
